/*
 * Helpers puros do Módulo A (Templates Meta): conversão nome → snake_case,
 * detecção de variáveis, conversão formulário ⇄ contrato da API e validação
 * client-side. Sem React aqui — fácil de testar e reutilizar.
 */

export const CATEGORIES = ["MARKETING", "UTILITY", "AUTHENTICATION"];
export const LANGUAGES = ["pt_BR", "en_US", "es"];
export const PARAMETER_FORMATS = ["POSITIONAL", "NAMED"];
export const HEADER_FORMATS = ["NONE", "TEXT", "IMAGE", "DOCUMENT", "VIDEO"];
export const BUTTON_TYPES = ["QUICK_REPLY", "URL", "PHONE_NUMBER", "COPY_CODE"];

export const LIMITS = {
  name: 512,
  headerText: 60,
  body: 1024,
  footer: 60,
  buttons: 10,
  buttonText: 25,
  urlButtons: 2,
  phoneButtons: 1,
  copyCodeButtons: 1,
  copyCode: 15,
  url: 2000,
  phone: 20,
};

// Status que a Meta deixa editar (1x a cada 24 h; volta para análise).
export const EDITABLE_STATUSES = ["APPROVED", "REJECTED", "PAUSED"];

/**
 * Variáveis sugeridas para cobrança. `example` é usado tanto no modo nomeado
 * ({{nome}}) quanto no posicional ({{n}} com o exemplo já preenchido).
 */
export const BILLING_VARIABLES = [
  { name: "nome", example: "Maria" },
  { name: "vencimento", example: "10/09/2026" },
  { name: "valor", example: "R$ 99,90" },
  {
    name: "linha_digitavel",
    example: "34191.79001 01043.510047 91020.150008 1 96610000009990",
  },
  { name: "pix", example: "00020126580014br.gov.bcb.pix0136a1b2c3d4-e5f6-7890-abcd-ef1234567890" },
  { name: "link", example: "https://exemplo.com.br/fatura/123" },
  { name: "contrato", example: "12345" },
];

const VARIABLE_RE = /\{\{\s*([A-Za-z0-9_]+)\s*\}\}/g;
const NAMED_RE = /^[a-z][a-z0-9_]*$/;
const URL_RE = /^https?:\/\/\S+$/i;

/** Nome do template: minúsculo, sem acento, só [a-z0-9_]. */
export function toSnakeCase(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[\s-]+/g, "_")
    .replace(/[^a-z0-9_]/g, "")
    .replace(/_{2,}/g, "_")
    .slice(0, LIMITS.name);
}

/** Lista única de variáveis na ordem em que aparecem no texto. */
export function extractVariables(text) {
  const found = [];
  const re = new RegExp(VARIABLE_RE.source, "g");
  let m = re.exec(String(text || ""));
  while (m) {
    if (!found.includes(m[1])) found.push(m[1]);
    m = re.exec(String(text || ""));
  }
  return found;
}

/** Maior índice usado no modo posicional ({{1}}, {{2}} …). */
export function nextPositionalIndex(text) {
  const nums = extractVariables(text)
    .map((v) => parseInt(v, 10))
    .filter((n) => Number.isInteger(n) && n > 0);
  return (nums.length ? Math.max(...nums) : 0) + 1;
}

/** Substitui as variáveis pelos exemplos (mantém {{x}} quando não houver). */
export function applyExamples(text, examples) {
  return String(text || "").replace(VARIABLE_RE, (whole, key) => {
    const ex = examples && examples[key];
    return ex !== undefined && ex !== null && String(ex) !== "" ? String(ex) : whole;
  });
}

/**
 * Converte o texto entre os dois formatos de variável.
 * NAMED → POSITIONAL: renumera na ordem de aparição.
 * POSITIONAL → NAMED: {{1}} vira {{variavel_1}}.
 * Devolve { text, examples } já migrados.
 */
export function convertVariables(text, examples, toFormat) {
  const vars = extractVariables(text);
  const map = {};
  vars.forEach((v, idx) => {
    if (toFormat === "POSITIONAL") {
      map[v] = String(idx + 1);
    } else {
      map[v] = /^\d+$/.test(v) ? `variavel_${v}` : v;
    }
  });
  const newText = String(text || "").replace(
    VARIABLE_RE,
    (whole, key) => `{{${map[key] || key}}}`
  );
  const newExamples = {};
  Object.keys(map).forEach((from) => {
    if (examples && examples[from] !== undefined) newExamples[map[from]] = examples[from];
  });
  return { text: newText, examples: newExamples };
}

export function emptyButton(type) {
  switch (type) {
    case "URL":
      return { type, text: "", url: "", example: "" };
    case "PHONE_NUMBER":
      return { type, text: "", phone_number: "" };
    case "COPY_CODE":
      return { type, example: "" };
    default:
      return { type: "QUICK_REPLY", text: "" };
  }
}

export function emptyForm(whatsappId) {
  return {
    id: null,
    status: null,
    whatsappId: whatsappId || "",
    name: "",
    category: "UTILITY",
    language: "pt_BR",
    parameterFormat: "POSITIONAL",
    headerFormat: "NONE",
    headerText: "",
    headerExample: "",
    headerVarExample: "",
    bodyText: "",
    bodyExamples: {},
    footerText: "",
    buttons: [],
  };
}

const firstString = (value) => {
  if (value === undefined || value === null) return "";
  if (typeof value === "string" || typeof value === "number") return String(value);
  if (Array.isArray(value)) return firstString(value[0]);
  if (typeof value === "object") {
    if (value.example !== undefined) return firstString(value.example);
    const keys = Object.keys(value);
    return keys.length ? firstString(value[keys[0]]) : "";
  }
  return "";
};

/**
 * Item da API (`components` no formato da Meta) → estado do formulário.
 * Tolerante a variações de `example` (string, array, objeto da Meta).
 */
export function fromApiTemplate(item) {
  const form = emptyForm(item?.whatsappId);
  if (!item) return form;
  form.id = item.id;
  form.status = item.status || null;
  form.name = item.name || "";
  form.category = CATEGORIES.includes(item.category) ? item.category : "UTILITY";
  form.language = item.language || "pt_BR";
  form.parameterFormat =
    item.parameterFormat === "NAMED" ? "NAMED" : "POSITIONAL";

  const components = Array.isArray(item.components) ? item.components : [];
  components.forEach((c) => {
    const type = String(c?.type || "").toUpperCase();
    if (type === "HEADER") {
      const fmt = String(c.format || "TEXT").toUpperCase();
      form.headerFormat = HEADER_FORMATS.includes(fmt) ? fmt : "TEXT";
      if (fmt === "TEXT") {
        form.headerText = c.text || "";
        const ex = c.example || {};
        form.headerVarExample = firstString(
          ex.header_text_named_params || ex.header_text || c.exampleText || ex
        );
      } else {
        const ex = c.example || {};
        form.headerExample = firstString(ex.header_handle || ex.header_url || ex);
      }
    } else if (type === "BODY") {
      form.bodyText = c.text || "";
      const vars = extractVariables(form.bodyText);
      const ex = c.example || {};
      const examples = {};
      if (Array.isArray(ex.body_text_named_params)) {
        ex.body_text_named_params.forEach((p) => {
          if (p && p.param_name) examples[p.param_name] = firstString(p.example);
        });
      } else if (Array.isArray(ex.body_text)) {
        const list = Array.isArray(ex.body_text[0]) ? ex.body_text[0] : ex.body_text;
        vars.forEach((v, idx) => {
          if (list[idx] !== undefined) examples[v] = String(list[idx]);
        });
      } else if (Array.isArray(c.examples)) {
        vars.forEach((v, idx) => {
          if (c.examples[idx] !== undefined) examples[v] = String(c.examples[idx]);
        });
      } else if (c.examples && typeof c.examples === "object") {
        Object.keys(c.examples).forEach((k) => {
          examples[k] = String(c.examples[k]);
        });
      }
      form.bodyExamples = examples;
    } else if (type === "FOOTER") {
      form.footerText = c.text || "";
    } else if (type === "BUTTONS") {
      const list = Array.isArray(c.buttons) ? c.buttons : [];
      form.buttons = list
        .map((b) => {
          const bt = String(b?.type || "").toUpperCase();
          if (!BUTTON_TYPES.includes(bt)) return null;
          const base = emptyButton(bt);
          if (bt === "URL") {
            return {
              ...base,
              text: b.text || "",
              url: b.url || "",
              example: firstString(b.example),
            };
          }
          if (bt === "PHONE_NUMBER") {
            return { ...base, text: b.text || "", phone_number: b.phone_number || "" };
          }
          if (bt === "COPY_CODE") {
            return { ...base, example: firstString(b.example) };
          }
          return { ...base, text: b.text || "" };
        })
        .filter(Boolean);
    }
  });
  return form;
}

/** Exemplos do corpo no formato do contrato (array posicional ou objeto nomeado). */
export function buildBodyExamples(form) {
  const vars = extractVariables(form.bodyText);
  if (form.parameterFormat === "NAMED") {
    const out = {};
    vars.forEach((v) => {
      out[v] = form.bodyExamples[v] || "";
    });
    return out;
  }
  const sorted = vars
    .map((v) => parseInt(v, 10))
    .filter((n) => Number.isInteger(n))
    .sort((a, b) => a - b);
  return sorted.map((n) => form.bodyExamples[String(n)] || "");
}

/** Estado do formulário → body do POST/PUT /meta-templates. */
export function toPayload(form) {
  const payload = {
    whatsappId: Number(form.whatsappId),
    name: toSnakeCase(form.name),
    category: form.category,
    language: form.language,
    parameterFormat: form.parameterFormat,
    body: {
      text: form.bodyText,
      examples: buildBodyExamples(form),
    },
  };
  if (form.headerFormat === "TEXT") {
    payload.header = { format: "TEXT", text: form.headerText };
    if (extractVariables(form.headerText).length) {
      payload.header.example = form.headerVarExample;
    }
  } else if (form.headerFormat !== "NONE") {
    payload.header = { format: form.headerFormat, example: form.headerExample };
  }
  if (form.footerText && form.footerText.trim()) {
    payload.footer = { text: form.footerText.trim() };
  }
  if (form.buttons.length) {
    payload.buttons = form.buttons.map((b) => {
      if (b.type === "URL") {
        const out = { type: "URL", text: b.text, url: b.url };
        if (extractVariables(b.url).length) out.example = b.example;
        return out;
      }
      if (b.type === "PHONE_NUMBER") {
        return { type: "PHONE_NUMBER", text: b.text, phone_number: b.phone_number };
      }
      if (b.type === "COPY_CODE") return { type: "COPY_CODE", example: b.example };
      return { type: "QUICK_REPLY", text: b.text };
    });
  }
  return payload;
}

/**
 * Validação client-side. `t` é a função de tradução (i18n.t). Devolve a lista
 * de mensagens; vazia = pode enviar.
 */
export function validateForm(form, t) {
  const errors = [];
  const push = (key, opts) => errors.push(t(`metaTemplates.validation.${key}`, opts));

  if (!form.whatsappId) push("whatsappRequired");

  const name = toSnakeCase(form.name);
  if (!name) push("nameRequired");
  else if (name !== String(form.name)) push("nameFormat");

  if (!CATEGORIES.includes(form.category)) push("categoryRequired");
  if (!LANGUAGES.includes(form.language)) push("languageRequired");

  // Cabeçalho
  if (form.headerFormat === "TEXT") {
    if (!form.headerText.trim()) push("headerTextRequired");
    if (form.headerText.length > LIMITS.headerText) {
      push("headerTextTooLong", { max: LIMITS.headerText });
    }
    const hv = extractVariables(form.headerText);
    if (hv.length > 1) push("headerOneVariable");
    if (hv.length === 1 && !String(form.headerVarExample || "").trim()) {
      push("headerExampleRequired");
    }
    if (hv.length === 1) {
      const bad =
        form.parameterFormat === "NAMED" ? !NAMED_RE.test(hv[0]) : hv[0] !== "1";
      if (bad) {
        if (form.parameterFormat === "NAMED") push("namedVariableFormat", { vars: hv[0] });
        else push("positionalSequence");
      }
    }
  } else if (form.headerFormat !== "NONE") {
    if (!URL_RE.test(String(form.headerExample || "").trim())) push("headerMediaExampleRequired");
  }

  // Corpo
  const body = String(form.bodyText || "");
  if (!body.trim()) push("bodyRequired");
  if (body.length > LIMITS.body) push("bodyTooLong", { max: LIMITS.body });
  const vars = extractVariables(body);
  if (vars.length) {
    if (form.parameterFormat === "NAMED") {
      const invalid = vars.filter((v) => !NAMED_RE.test(v));
      if (invalid.length) push("namedVariableFormat", { vars: invalid.join(", ") });
    } else {
      const nums = vars.map((v) => (/^\d+$/.test(v) ? parseInt(v, 10) : NaN));
      const sorted = nums.filter((n) => !Number.isNaN(n)).sort((a, b) => a - b);
      const sequential =
        nums.every((n) => !Number.isNaN(n)) &&
        sorted.every((n, idx) => n === idx + 1);
      if (!sequential) push("positionalSequence");
    }
    const missing = vars.filter((v) => !String(form.bodyExamples[v] || "").trim());
    if (missing.length) {
      push("bodyExampleRequired", {
        vars: missing.map((v) => `{{${v}}}`).join(", "),
      });
    }
  }

  // Rodapé
  if (form.footerText.length > LIMITS.footer) push("footerTooLong", { max: LIMITS.footer });

  // Botões
  const buttons = Array.isArray(form.buttons) ? form.buttons : [];
  if (buttons.length > LIMITS.buttons) push("tooManyButtons", { max: LIMITS.buttons });
  const count = (type) => buttons.filter((b) => b.type === type).length;
  if (count("URL") > LIMITS.urlButtons) push("tooManyUrlButtons", { max: LIMITS.urlButtons });
  if (count("PHONE_NUMBER") > LIMITS.phoneButtons) push("tooManyPhoneButtons");
  if (count("COPY_CODE") > LIMITS.copyCodeButtons) push("tooManyCopyCodeButtons");

  // Respostas rápidas precisam ficar agrupadas (regra da Meta).
  const kinds = buttons.map((b) => (b.type === "QUICK_REPLY" ? "q" : "c"));
  let switches = 0;
  for (let i = 1; i < kinds.length; i += 1) if (kinds[i] !== kinds[i - 1]) switches += 1;
  if (switches > 1) push("buttonsGrouped");

  const texts = new Set();
  buttons.forEach((b, idx) => {
    const n = idx + 1;
    if (b.type !== "COPY_CODE") {
      const text = String(b.text || "").trim();
      if (!text) push("buttonTextRequired", { n });
      else if (text.length > LIMITS.buttonText) {
        push("buttonTextTooLong", { n, max: LIMITS.buttonText });
      }
      if (text) {
        const key = text.toLowerCase();
        if (texts.has(key)) push("buttonTextDuplicate", { n });
        texts.add(key);
      }
    }
    if (b.type === "URL") {
      const url = String(b.url || "").trim();
      if (!URL_RE.test(url)) push("buttonUrlInvalid", { n });
      const uv = extractVariables(url);
      if (uv.length > 1 || (uv.length === 1 && !/\{\{\s*1\s*\}\}$/.test(url))) {
        push("buttonUrlVariable", { n });
      }
      if (uv.length === 1 && !String(b.example || "").trim()) push("buttonUrlExampleRequired", { n });
    }
    if (b.type === "PHONE_NUMBER") {
      const phone = String(b.phone_number || "").replace(/\s/g, "");
      if (!/^\+?\d{8,20}$/.test(phone)) push("buttonPhoneInvalid", { n });
    }
    if (b.type === "COPY_CODE") {
      const code = String(b.example || "").trim();
      if (!code) push("buttonCopyCodeRequired", { n });
      else if (code.length > LIMITS.copyCode) push("buttonCopyCodeTooLong", { n, max: LIMITS.copyCode });
    }
  });

  return errors;
}

/** Mensagem de erro vinda da API (400 { error }) ou genérica. */
export function apiErrorMessage(err, fallback) {
  const msg = err?.response?.data?.error || err?.response?.data?.message;
  if (typeof msg === "string" && msg.trim()) return msg;
  if (typeof err?.message === "string" && err.message && !err.response) return err.message;
  return fallback;
}
